/**
 * MCP クライアントごとの接続手順。
 *
 * 画面だけの表示物なので API は持たない。テナントごとに変わらず、
 * 差し込む値はエンドポイントとトークンの 2 つだけ。
 *
 * **書式は 2 通りある。** OAuth に対応したクライアントは URL だけでよく
 * (初回の接続でブラウザが開き、同意画面が出る)、対応していないクライアントは
 * 画面で発行したトークンをヘッダーに書く。**違いはトークンの行の有無だけ**なので、
 * 同じ書式を 2 つ並べて持つ。
 *
 * 書式はすべて各社の公式ドキュメントで確認したもの (2026-08-14 時点)。
 * 各社の設定 UI は変わるので、直すときは必ず出典に当たり直す。
 */

export interface McpClient {
  id: string;
  label: string;
  /** 貼り付け先のファイル。コマンドで完結するものは null */
  target: string | null;
  /** トークンを貼る書式 (OAuth に対応していないクライアント向け) */
  snippet: (endpoint: string, token: string) => string;
  /**
   * ブラウザで許可する書式 (OAuth)。**トークンを書かないだけ**で、
   * 残りは同じ。初回の接続でクライアントがブラウザを開き、同意画面が出る。
   */
  oauthSnippet: (endpoint: string) => string;
  /** 補足。2〜3 行に収める */
  notes: string[];
}

/** トークンが手元に無いときに見せる差し込み文字列 */
export const TOKEN_PLACEHOLDER = 'YOUR_TOKEN';

export const CLAUDE_CODE: McpClient = {
  id: 'claude-code',
  label: 'Claude Code',
  target: null,
  snippet: (endpoint, token) =>
    `claude mcp add --transport http --scope user inou ${endpoint} \\\n  --header "Authorization: Bearer ${token}"`,
  oauthSnippet: (endpoint) => `claude mcp add --transport http --scope user inou ${endpoint}`,
  notes: [
    '--scope user にすると、どのリポジトリからでも使えます。',
    'claude mcp list で接続を確認できます。',
  ],
};

export const CODEX: McpClient = {
  id: 'codex',
  label: 'Codex',
  target: '~/.codex/config.toml',
  snippet: (endpoint, token) =>
    `[mcp_servers.inou]\nurl = "${endpoint}"\nhttp_headers = { "Authorization" = "Bearer ${token}" }`,
  oauthSnippet: (endpoint) => `[mcp_servers.inou]\nurl = "${endpoint}"`,
  notes: [
    'ファイルが無ければ新しく作ります。リポジトリ単位にするなら .codex/config.toml。',
    'トークンを直書きしたくなければ、env_http_headers = { "Authorization" = "INOU_TOKEN" } にして環境変数から読ませます。',
  ],
};

export const COPILOT: McpClient = {
  id: 'copilot',
  label: 'Copilot',
  target: '.vscode/mcp.json',
  snippet: (endpoint, token) =>
    `{\n  "servers": {\n    "inou": {\n      "type": "http",\n      "url": "${endpoint}",\n      "headers": { "Authorization": "Bearer ${token}" }\n    }\n  }\n}`,
  oauthSnippet: (endpoint) =>
    `{\n  "servers": {\n    "inou": {\n      "type": "http",\n      "url": "${endpoint}"\n    }\n  }\n}`,
  notes: [
    'ユーザー全体に入れるなら、コマンドパレットの MCP: Open User Configuration から同じ内容を書きます。',
  ],
};

export const CURSOR: McpClient = {
  id: 'cursor',
  label: 'Cursor',
  target: '~/.cursor/mcp.json',
  snippet: (endpoint, token) =>
    `{\n  "mcpServers": {\n    "inou": {\n      "url": "${endpoint}",\n      "headers": { "Authorization": "Bearer ${token}" }\n    }\n  }\n}`,
  oauthSnippet: (endpoint) =>
    `{\n  "mcpServers": {\n    "inou": {\n      "url": "${endpoint}"\n    }\n  }\n}`,
  notes: ['リポジトリ単位にするなら .cursor/mcp.json に置きます。'],
};

export const CLAUDE_DESKTOP: McpClient = {
  id: 'claude-desktop',
  label: 'Claude Desktop',
  target: 'claude_desktop_config.json',
  snippet: (endpoint, token) =>
    `{\n  "mcpServers": {\n    "inou": {\n      "command": "npx",\n      "args": [\n        "-y",\n        "mcp-remote",\n        "${endpoint}",\n        "--header",\n        "Authorization:\${AUTH_HEADER}"\n      ],\n      "env": { "AUTH_HEADER": "Bearer ${token}" }\n    }\n  }\n}`,
  oauthSnippet: (endpoint) =>
    `{\n  "mcpServers": {\n    "inou": {\n      "command": "npx",\n      "args": ["-y", "mcp-remote", "${endpoint}"]\n    }\n  }\n}`,
  notes: [
    'Claude Desktop はリモート MCP に自前のヘッダーを渡せないので、mcp-remote を挟みます。',
    'Authorization: の後ろにスペースを入れず値を env に置くのは、Windows で args 内のスペースが壊れる不具合を避けるためです。',
    '書き換えたら Claude Desktop を再起動してください。',
  ],
};

export const MCP_CLIENTS: readonly McpClient[] = [
  CLAUDE_CODE,
  CODEX,
  COPILOT,
  CURSOR,
  CLAUDE_DESKTOP,
];
